/**
 * Hex-map facing helpers — mirrors scene `approach-facing-rad` /
 * `facingRadFromMoveDelta` (0 = screen-up) so module-map dolls share the same
 * 360° yaw language while translation stays tile-restricted.
 */

/** Default in-place yaw approach before a hex hop (ms). */
export const HEX_APPROACH_FACING_DURATION_MS = 280;

/** Treat smaller deltas as already facing. */
export const HEX_APPROACH_FACING_EPS_RAD = 0.02;

export function normalizeFacingRad(rad: number): number {
	if (!Number.isFinite(rad)) {
		return 0;
	}
	const twoPi = Math.PI * 2;
	let n = rad % twoPi;
	if (n < 0) {
		n += twoPi;
	}
	return n;
}

/** Shortest signed delta from `fromRad` → `toRad` in (−π, π]. */
export function shortestFacingDeltaRad(fromRad: number, toRad: number): number {
	const from = normalizeFacingRad(fromRad);
	const to = normalizeFacingRad(toRad);
	let delta = to - from;
	if (delta > Math.PI) {
		delta -= Math.PI * 2;
	} else if (delta <= -Math.PI) {
		delta += Math.PI * 2;
	}
	return delta;
}

export function approachFacingRad(input: {
	fromRad: number;
	toRad: number;
	t: number;
}): number {
	const from = normalizeFacingRad(input.fromRad);
	const delta = shortestFacingDeltaRad(from, input.toRad);
	const t = Math.min(1, Math.max(0, input.t));
	return normalizeFacingRad(from + delta * t);
}

/**
 * Screen-space move delta → facing (0 = up). Same as scene
 * `facingRadFromMoveDelta` (`atan2(dx, -dy)`).
 */
export function facingRadFromScreenDelta(
	dx: number,
	dy: number,
	minLen = 1e-6,
): number | null {
	const len = Math.hypot(dx, dy);
	if (len < minLen) {
		return null;
	}
	return normalizeFacingRad(Math.atan2(dx, -dy));
}

export function stepApproachFacing(input: {
	fromRad: number;
	toRad: number;
	elapsedMs: number;
	durationMs?: number;
	reducedMotion?: boolean;
}): {
	facingRad: number;
	elapsedMs: number;
	complete: boolean;
	t: number;
} {
	const durationMs = Math.max(
		0,
		input.durationMs ?? HEX_APPROACH_FACING_DURATION_MS,
	);
	const delta = Math.abs(shortestFacingDeltaRad(input.fromRad, input.toRad));
	if (
		input.reducedMotion === true ||
		durationMs <= 0 ||
		delta < HEX_APPROACH_FACING_EPS_RAD
	) {
		return {
			facingRad: normalizeFacingRad(input.toRad),
			elapsedMs: durationMs,
			complete: true,
			t: 1,
		};
	}
	const elapsedMs = Math.max(0, input.elapsedMs);
	const t = Math.min(1, elapsedMs / durationMs);
	return {
		facingRad: approachFacingRad({
			fromRad: input.fromRad,
			toRad: input.toRad,
			t,
		}),
		elapsedMs,
		complete: t >= 1,
		t,
	};
}
